import React, { useContext, useLayoutEffect, useState } from 'react';
import { scroller } from 'react-scroll';
import { navigate } from 'gatsby';
import { useMediaQuery, useTheme, styled } from '@mui/material';
import GlobalHeader from './GlobalHeader';
import HeaderLink from './HeaderLink';
import Select from '../select-v2';
import { getLaptop, useGlobalState, useDispatch } from '../GlobalState';
import { AddToCartButton } from '../addToCartButton';
import { CartContext } from '../../context/CartContext';

const StyledDropdownLink = styled('a')`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  text-decoration: none;
  color: ${({ theme }) => theme.palette.greyscale.lightGrey};
  font-weight: bold;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 1.2px;
  &.current,
  &:active,
  &:hover {
    color: ${({ theme }) => theme.palette.greyscale.white};
  }
`;

const CustomHeader = styled(GlobalHeader)`
  &.MuiAppBar-root {
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(150px);

    &.active:after,
    .active:after {
      bottom: -20px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
      min-height: 48px;

      .MuiToolbar-root {
        min-height: 48px;
      }
    }
  }
  .MuiFormControl-root {
    width: auto;
    .MuiInput-root {
      padding: 4px 0 3px;
      svg {
        transform: scale(0.75);
      }
      .MuiSelect-select {
        padding-right: 0;
      }
    }
  }
`;

interface PageHeaderProps {
  links:
    | {
        name: string;
        url: string;
      }[]
    | undefined;
  hideBuyButton?: boolean;
}

const CustomSelect = styled(Select)`
  &::before,
  &.MuiInput-underline.Mui-focused:after {
    display: none;
  }
  .MuiInput-input:focus {
    background-color: transparent;
  }
  .MuiInput-input:focus-visible {
    outline: 1px solid ${({ theme }) => theme.palette.cyan};
    /* The below styles are to visually center the text in the focus outline */
    position: relative;
    padding-left: 6px;
    margin-left: -6px;
  }
`;

const BuyButton = styled(AddToCartButton)`
  margin-left: 1rem;
`;

const PageHeader: React.FC<PageHeaderProps> = ({ links, hideBuyButton }) => {
  const [{ products, currentPage }] = useGlobalState();
  const [jumpTo, setJumpto] = useState('');
  const dispatch = useDispatch();
  const { myCart, setShowCart } = useContext(CartContext);
  const laptop = getLaptop(products);
  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );

  // eslint-disable-next-line no-param-reassign
  links = links?.filter((obj) => obj.name !== 'FAQ');
  // eslint-disable-next-line no-param-reassign
  links = links?.filter((obj) => obj.name !== 'Specs');

  const scrollerOptions = {
    duration: 500,
    delay: 0,
    smooth: 'easeInOutQuart',
  };

  // Scroll to target by query string on page render.
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scrollTo = params.get('scrollto');
    if (scrollTo) {
      // Give the images some time to load, otherwise the page jumps around after scrolling
      // and the intended target is no longer visible. window.onload has already fired.
      setTimeout(() => {
        scroller.scrollTo(scrollTo, scrollerOptions);
      }, 200);
    }
  }, []);

  useLayoutEffect(() => {
    if (jumpTo?.length) {
      try {
        const position = jumpTo.slice(jumpTo.indexOf('/#') + 2, jumpTo.length); // Get rid of /#
        // Doesn't work without a setTimeout for unknown reasons ¯\_(ツ)_/¯
        setTimeout(() => {
          scroller.scrollTo(position, scrollerOptions);
        }, 1);
      } catch (error) {}
    }
  }, [jumpTo]);

  return (
    <CustomHeader
      left={
        isMobile ? (
          <CustomSelect
            value={currentPage}
            MenuProps={{
              sx: {
                '.MuiPaper-root': {
                  background: 'rgba(0, 0, 0, 0.4)',
                  backdropFilter: 'blur(150px)',
                  width: '100%',
                  maxWidth: '100%',
                  marginLeft: '-1rem',
                  marginTop: '3px',
                  borderRadius: '0',
                  borderTop: `2px solid ${theme?.palette.greyscale['07']}`,
                },
                '.MuiMenuItem-root': {
                  minHeight: 'auto',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                },
                '.MuiMenu-list': {
                  '.Mui-selected, .Mui-selected.Mui-focusVisible': {
                    backgroundColor: 'transparent',
                    borderLeft: `2px solid ${theme.palette.greyscale['04']}`,
                    paddingLeft: '14px',
                  },
                  '.Mui-selected:hover, .Mui-selected.Mui-focusVisible:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            }}
          >
            {links?.map(
              ({ name, url }) =>
                name &&
                url && (
                  <Select.Option
                    value={url}
                    key={`${name}-${url}`}
                    onClick={() => {
                      // Select.Option has a tabindex so we handle clicks here instead of the <a> child.
                      setJumpto(url);
                      dispatch({
                        type: 'PAGE_SET_CURRENT',
                        currentPage: url,
                      });
                    }}
                  >
                    <StyledDropdownLink
                      className={`dropdown-link ${
                        currentPage.includes(url) ? 'current' : ''
                      }`}
                    >
                      {name}
                    </StyledDropdownLink>
                  </Select.Option>
                ),
            )}
          </CustomSelect>
        ) : null
      }
      center={
        !isMobile ? (
          <>
            {links?.map(
              ({ name, url }) =>
                name &&
                url && (
                  <HeaderLink key={`${name}-${url}`} title={name} to={url} />
                ),
            )}
            {/* {!hideBuyButton && laptop && (
              <BuyButton
                variant="primary"
                color="cyan"
                text="Order Now"
                product={laptop}
                addToCartAction={() => {
                  if (laptop) {
                    dispatch({
                      type: 'CART_ADD',
                      product: { ...laptop, quantity: 1 },
                    });
                    // navigate('/cart');
                    setShowCart(true);
                  }
                }}
              />
            )} */}
          </>
        ) : null
      }
   
        // isMobile && !hideBuyButton && laptop ? (
        //   <BuyButton
        //     variant="primary"
        //     color="cyan"
        //     text="Order Now"
        //     product={laptop}
        //     addToCartAction={() => {
        //       if (laptop) {
        //         dispatch({
        //           type: 'CART_ADD',
        //           product: { ...laptop, quantity: 1 },
        //         });
        //         // navigate('/cart');
        //         setShowCart(true);
        //       }
        //     }}
        //   />
        // ) : null
   
    />
  );
};

export default PageHeader;
